import React from 'react';
import { shallowEqual } from 'react-redux';
import { Box } from '@apia/theme';
import conf from '../../../../../classes/configController';
import { useAppSelector } from '../../../../../hooks/hooks';
import useContext from '../../../../../hooks/useContext';
import store from '../../../../../store';
import { dataGridActions } from '../../../../../store/dataGridSlice/dataGridSlice';
import propsStore from '../../../../../store/propsStore/propsStore';
import useDomProps from '../../../../../store/selection/useDomProps';
import { noNaN } from '../../../../../utils/util';
import { IconButton } from '../../../../common';
import { icons } from '../../../../common/Icon';
import fieldsStore from '../../fieldsStore/fieldsStore';
import Cell from './Cell';
import { DataGridIdentifier, DataGridRowIdentifier } from './context';
import { getLabel } from '@apia/util';

interface IRow {
  rowIndex: number;
}

const Row = ({ rowIndex }: IRow) => {
  const { gridId } = useContext(DataGridIdentifier);

  const {
    columns,
    hasEditionForm,
    isStoreShownDeleteButton,
    isSomeDeleteShown,
    isSomeEditFormShown,
    shouldHideDeleteButton,
    startIndex,
  } = useAppSelector((global) => {
    const state = global.dataGridSlice[gridId];
    const grid = state.context.grid.field;
    const props = propsStore.getFieldProps(gridId);

    const startIndexInner = noNaN(grid.startIndex);

    let isSomeEditFormShownInner = false;
    for (
      let i = startIndexInner;
      i < state.rows.length + startIndexInner;
      i++
    ) {
      if (state.showEditFormButtons[i] !== false) {
        isSomeEditFormShownInner = true;
        break;
      }
    }

    return {
      columns: state.columns,
      hasEditionForm:
        !!grid.properties.gridForm &&
        state.showEditFormButtons[rowIndex + startIndexInner] !== false,
      startIndex: startIndexInner,
      isFocused: state.isGridFocused && state.focusedRow === rowIndex,
      isSomeDeleteShown: state.showDeleteButtonsCount > 0,
      isSomeEditFormShown:
        !!grid.properties.gridForm && isSomeEditFormShownInner,
      shouldHideDeleteButton:
        [
          grid.properties.hideIncludeButton,
          grid.properties.hideDelButton,
          grid.properties.hideGridButtons,
          state.context.form.isFormReadOnly,
          props?.readonly,
        ].find((current) => String(current) === 'true') ||
        grid.properties.gridQuery,
      isStoreShownDeleteButton:
        state.showDeleteButtons[rowIndex + startIndexInner],
    };
  }, shallowEqual);

  const deleteButton = conf(
    'execution.components.dataGrid.deleteButtonIcon',
    'Close',
  );

  const shouldShowAdditionalCell =
    hasEditionForm ||
    isSomeDeleteShown ||
    isSomeEditFormShown ||
    (!shouldHideDeleteButton &&
      conf('execution.components.dataGrid.showDeleteButton', false));
  const shouldShowDeleteButton =
    isStoreShownDeleteButton ||
    (isStoreShownDeleteButton !== false &&
      !shouldHideDeleteButton &&
      conf('execution.components.dataGrid.showDeleteButton', false));

  const domProps = useDomProps(
    'tr',
    {
      isThisRowExpanded: false,
      rowIndex,
    },
    (global) => global.dataGridSlice[gridId],
  );
  delete domProps['aria-expanded'];

  const deleteIcon = deleteButton === 'Close' ? icons.Close : icons.Trash;

  const handleEditButton = React.useCallback(() => {
    store.dispatch(
      dataGridActions.update({
        id: gridId,
        editionFormRow: rowIndex + startIndex,
      }),
    );
  }, [gridId, rowIndex, startIndex]);
  const handleDeleteButton = React.useCallback(() => {
    fieldsStore.getById(gridId).fireEvent('gridDelete');
  }, [gridId]);

  React.useEffect(() => {
    function handleBeforePrint() {
      const printTable = document.querySelector(
        `#printRow__${gridId}__${rowIndex} table tbody`,
      );
      const originalRow = document.getElementById(
        `commonRow__${gridId}__${rowIndex}`,
      );
      if (printTable && originalRow) {
        printTable.innerHTML = '';
        originalRow
          .querySelectorAll('td:not(.additional__column)')
          .forEach((current, index) => {
            const newCell = current.cloneNode(true);
            const newRow = document.createElement('tr');
            newRow.innerHTML = `<th>${columns[index].label}</th>${
              (newCell as HTMLElement).innerHTML
            }`;
            printTable.append(newRow);
          });
      } else
        console.warn(`Row ${gridId} cannot be printed due to missing elements`);
    }

    window.addEventListener('beforeprint', handleBeforePrint);
    return () => window.removeEventListener('beforeprint', handleBeforePrint);
  }, [columns, gridId, rowIndex]);

  return (
    <DataGridRowIdentifier rowIndex={rowIndex}>
      <tr
        aria-hidden
        className="dataGrid__printRow"
        id={`printRow__${gridId}__${rowIndex}`}
      >
        <td colSpan={columns.length}>
          <table
            className="dataGrid__printTable"
            data-variant="layout.common.tables.primary"
            sx={{ variant: 'layout.common.tables.primary' }}
          >
            <tbody />
          </table>
        </td>
      </tr>
      <tr
        className="grid__row"
        id={`commonRow__${gridId}__${rowIndex}`}
        {...domProps}
      >
        {shouldShowAdditionalCell && (
          <td className="additional__column grid__cell">
            <Box className="additional__column__buttons">
              {hasEditionForm && (
                <IconButton
                  icon={icons.Edit}
                  onClick={handleEditButton}
                  aria-label={window.GNR_NAV_ADM_UPDATE}
                />
              )}
              {shouldShowDeleteButton && (
                <IconButton
                  icon={deleteIcon}
                  onClick={handleDeleteButton}
                  aria-label={getLabel('btnDel').text}
                />
              )}
            </Box>
          </td>
        )}
        {columns.map((column, columnIndex) => (
          <Cell key={column.id} columnIndex={columnIndex} />
        ))}
      </tr>
    </DataGridRowIdentifier>
  );
};

export default React.memo(Row);
